import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	AbcdQuestionAnswerDesktop,
	AbcdQuestionAnswerMobile,
	AssociationQuestionAnswerDesktop,
	AssociationQuestionAnswerMobile,
	ConnectionQuestionAnswerDesktop,
	ConnectionQuestionAnswerMobile,
	EstimationQuestionAnswerDesktop,
	EstimationQuestionAnswerMobile,
	StandardQuestionAnswerDesktop,
	StandardQuestionAnswerMobile,
	VisualQuestionAnswerDesktop,
	VisualQuestionAnswerMobile,
} from '@quizzach/layouts';
import { Colors } from '@quizzach/styles';
import { QuestionType } from '@quizzach/interfaces';
import { usePreviewQuestion } from '@quizzach/api-hooks';
import { scrollToTop } from '@quizzach/utils';
import { useWindowListener } from '@quizzach/contexts';

import {
	AdminPreviewStage,
	PREVIEW_QUESTION_NUMBER,
	PREVIEW_TOTAL_QUESTIONS,
} from './AdminPreviewStage';

const ANSWER_COMPONENTS = {
	[QuestionType.standard]: {
		Mobile: StandardQuestionAnswerMobile,
		Desktop: StandardQuestionAnswerDesktop,
	},
	[QuestionType.abcd]: {
		Mobile: AbcdQuestionAnswerMobile,
		Desktop: AbcdQuestionAnswerDesktop,
	},
	[QuestionType.association]: {
		Mobile: AssociationQuestionAnswerMobile,
		Desktop: AssociationQuestionAnswerDesktop,
	},
	[QuestionType.connection]: {
		Mobile: ConnectionQuestionAnswerMobile,
		Desktop: ConnectionQuestionAnswerDesktop,
	},
	[QuestionType.visual]: {
		Mobile: VisualQuestionAnswerMobile,
		Desktop: VisualQuestionAnswerDesktop,
	},
	[QuestionType.estimation]: {
		Mobile: EstimationQuestionAnswerMobile,
		Desktop: EstimationQuestionAnswerDesktop,
	},
} as const;

export const AdminQuizAnswerPreview = () => {
	const { id } = useParams<{ id: string }>();
	const { question, neighbors } = usePreviewQuestion(id);
	const navigate = useNavigate();
	const { touchDevice } = useWindowListener();

	// Browsing runs question → answer → next question through the whole
	// database; forward from the last answer just stays put.
	const goToQuestion = () => {
		navigate(`/preview/question/${id}`);
	};
	const goToNextQuestion = () => {
		if (neighbors?.next != null) {
			navigate(`/preview/question/${neighbors.next}`);
		}
	};

	useEffect(() => {
		scrollToTop();
	}, [id]);

	if (!question) return null;

	const renderAnswerComponent = () => {
		if (!question.type || !ANSWER_COMPONENTS[question.type]) {
			return null;
		}

		const componentVariant = touchDevice ? 'Mobile' : 'Desktop';
		const AnswerComponent = ANSWER_COMPONENTS[question.type][
			componentVariant
		] as any;

		// Same containers as the redesigned play answer slides: the question
		// sits bare on the backdrop, only the answer keeps its container.
		const commonProps = {
			questionContainerColor: Colors.none,
			answerContainerColor: Colors.answerContainer,
			currentQuestion: question,
		};

		// Desktop estimation doesn't have currentQuestionNumber
		if (!touchDevice && question.type === QuestionType.estimation) {
			return <AnswerComponent {...commonProps} />;
		}

		const position = neighbors?.position ?? PREVIEW_QUESTION_NUMBER;
		const deviceSpecificProps = touchDevice
			? {
					currentQuestionNumber: position,
					totalQuestions: neighbors?.total ?? PREVIEW_TOTAL_QUESTIONS,
				}
			: {
					currentQuestionNumber: position,
				};

		return <AnswerComponent {...commonProps} {...deviceSpecificProps} />;
	};

	return (
		<AdminPreviewStage
			question={question}
			dataId='slides-answer-preview'
			onNext={goToNextQuestion}
			onPrevious={goToQuestion}
			position={neighbors?.position}
			total={neighbors?.total}
		>
			{renderAnswerComponent()}
		</AdminPreviewStage>
	);
};
